import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { useAdminStatsQuery, useAdminTimeseriesQuery, useAdminTopAgentsQuery } from '@shared/api';
import { agentLabel, ANNOUNCEABLE_AGENT_CODES } from '@shared/agents/labels';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const MetricCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const MetricLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const MetricValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetricCaption = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing(4)};
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing(5)};
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const ChartTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const RangeButtons = styled.div`
  display: inline-flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 2px;
`;

const RangeBtn = styled(Button)<{ $active: boolean }>`
  height: 28px;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.backgroundElevated : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  border: none;
  box-shadow: ${({ $active, theme }) => ($active ? theme.shadow.xs : 'none')};
  &:hover:not(:disabled) {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.backgroundElevated : theme.colors.surfaceHover};
    transform: none;
  }
`;

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export function AdminDashboardTab() {
  const theme = useTheme();
  const [range, setRange] = useState<7 | 30>(7);
  const { data: stats } = useAdminStatsQuery(undefined, {
    pollingInterval: 30_000,
  });
  const { data: ts } = useAdminTimeseriesQuery({ days: range });
  const { data: agents } = useAdminTopAgentsQuery();

  const chartData = (ts?.items ?? []).map((p) => ({
    date: p.date.slice(5), // MM-DD
    requests: p.requests,
    errors: p.errors,
  }));

  // Показываем ВЕСЬ ростер реальных агентов (KNOWN_AGENT_CODES без диспетчера),
  // дозаполняя нулями тех, у кого не было запросов за 7 дней — чтобы список
  // агентов был полным, а не «только тот, кто что-то делал». Подписи берём из
  // единого справочника AGENT_LABELS, а не сырые коды вроде `statut`.
  const countByCode = new Map(
    (agents?.items ?? []).map((a) => [a.agent_code, a.count] as const),
  );
  const agentsData = ANNOUNCEABLE_AGENT_CODES.filter((code) => code !== 'dispatcher')
    .map((code) => ({ agent: agentLabel(code), count: countByCode.get(code) ?? 0 }))
    .sort((a, b) => b.count - a.count);

  return (
    <>
      <Grid>
        <MetricCard>
          <MetricLabel>Пользователей</MetricLabel>
          <MetricValue>{stats?.users_total ?? '—'}</MetricValue>
          <MetricCaption>активных: {stats?.users_active ?? 0}</MetricCaption>
        </MetricCard>
        <MetricCard>
          <MetricLabel>LLM-запросов · 24ч</MetricLabel>
          <MetricValue>{formatNumber(stats?.llm_requests_24h ?? 0)}</MetricValue>
          <MetricCaption>
            {stats?.unique_users_24h ?? 0} {stats?.unique_users_24h === 1 ? 'юзер' : 'юзеров'}
          </MetricCaption>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Ошибки · 24ч</MetricLabel>
          <MetricValue>{stats?.llm_errors_24h ?? 0}</MetricValue>
          <MetricCaption>error rate: {(stats?.error_rate_24h ?? 0).toFixed(1)}%</MetricCaption>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Средний latency</MetricLabel>
          <MetricValue>{Math.round(stats?.avg_latency_ms_24h ?? 0)} ms</MetricValue>
          <MetricCaption>за 24ч</MetricCaption>
        </MetricCard>
      </Grid>

      <ChartsRow>
        <ChartCard>
          <ChartHeader>
            <ChartTitle>Запросы по дням</ChartTitle>
            <RangeButtons>
              <RangeBtn
                $variant="ghost"
                $size="sm"
                $active={range === 7}
                onClick={() => setRange(7)}
              >
                7 дней
              </RangeBtn>
              <RangeBtn
                $variant="ghost"
                $size="sm"
                $active={range === 30}
                onClick={() => setRange(30)}
              >
                30 дней
              </RangeBtn>
            </RangeButtons>
          </ChartHeader>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="requests-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.colors.primary} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={theme.colors.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,128,0.15)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="rgba(120,120,128,0.5)" />
              <YAxis tick={{ fontSize: 11 }} stroke="rgba(120,120,128,0.5)" />
              <Tooltip
                contentStyle={{
                  background: 'rgba(20,20,24,0.95)',
                  border: '1px solid rgba(120,120,128,0.3)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#fff',
                }}
              />
              <Area
                type="monotone"
                dataKey="requests"
                stroke={theme.colors.primary}
                strokeWidth={2}
                fill="url(#requests-gradient)"
                animationDuration={500}
              />
              <Area
                type="monotone"
                dataKey="errors"
                stroke="#F57C00"
                strokeWidth={1.5}
                fillOpacity={0}
                animationDuration={500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartHeader>
            <ChartTitle>Топ агенты (7 дней)</ChartTitle>
          </ChartHeader>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={agentsData}
              layout="vertical"
              margin={{ top: 8, right: 12, left: 8, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(120,120,128,0.15)"
                horizontal={false}
              />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="rgba(120,120,128,0.5)" />
              <YAxis
                type="category"
                dataKey="agent"
                width={150}
                tick={{ fontSize: 12 }}
                stroke="rgba(120,120,128,0.5)"
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(20,20,24,0.95)',
                  border: '1px solid rgba(120,120,128,0.3)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#fff',
                }}
              />
              <Bar dataKey="count" fill={theme.colors.primary} radius={[0, 4, 4, 0]} animationDuration={500} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsRow>
    </>
  );
}
